/**
 * Phase F.1b / GEN-2998 — comparator-driven projection of the
 * `OrderInProgress` family section.
 *
 * `deriveOrderInProgressFamilyRows` (the source of truth) keeps deciding
 * which family rows render and which flags they carry — this helper
 * **never** re-implements that logic. It only consumes the resulting
 * `OrderInProgressFamilyDisplay` plus the canonical `comparators[]`
 * array, pairs each BIOLOGICAL_MOTHER / BIOLOGICAL_FATHER row with the
 * matching `Comparator`, and produces fully-prepared `<ComparatorCard>`
 * props so the flag-ON render path stays a thin map.
 *
 * GUARDIAN_CONTACT rows are intentionally NOT projected here: the legacy
 * `<GuardianContactCard>` consumes `co.guardianContact` (not a
 * `Comparator`), so the OrderInProgress wire-up renders the guardian row
 * the same way in both flag states. The `<ComparatorList>` component
 * delegates guardian rendering back to a caller-provided callback.
 *
 * A proband-only BIOLOGICAL_MOTHER / BIOLOGICAL_FATHER row is the one
 * exception where `co` matters: that parent is the consenting guardian and
 * is not sequenced, so no `Comparator` for them is ever persisted and their
 * details live on `co.guardianContact`. Such a row is hydrated from there
 * (see `guardianBackedComparatorForParentRow`).
 *
 * Safety contract (Q4.A in the F.1b ARCHITECT plan):
 *   - When `comparators[]` is missing / empty OR any BIOLOGICAL_MOTHER /
 *     BIOLOGICAL_FATHER row has no matching `Comparator`, the helper
 *     returns `fallbackToLegacy: true`. The OrderInProgress flag-ON
 *     branch must then defer to the legacy `<FamilyCard>` JSX so the
 *     section never goes blank.
 */

import type {
  CanonicalGuardianContact,
  CanonicalOrderPayload,
  Comparator,
  ComparatorDemographic,
  FamilyMemberRelationship,
} from './canonicalOrder'
import type { OrderInProgressFamilyDisplay, OrderInProgressFamilyRow } from './orderInProgressFamilyCards'

export type ComparatorListParentItem = {
  rowKind: 'BIOLOGICAL_MOTHER' | 'BIOLOGICAL_FATHER'
  /** Canonical row identity used to wire onEdit back to the legacy modals. */
  editTarget: 'mother' | 'father'
  comparator: Comparator
  /** Card flags derived from the family-row + comparator data. */
  isPrimaryGuardian: boolean
  /**
   * Bottom status pill message. Resolved by the projection:
   *   - non-test-subject (proband-only) rows: "Not included" or,
   *     when treated as tested with content, the success label.
   *   - tested-parent rows with content: the caller's success label
   *     (`successStatusText` — draft "Complete" vs post-submit
   *     "Sent to lab").
   *   - tested-parent rows without content: "Incomplete" (needsAttention).
   */
  statusMessage?: string
  statusVariant?: 'success' | 'info'
  /**
   * `true` when this row's person comes from `co.guardianContact` rather than
   * from a `Comparator` (a proband-only parent who is only the consenting
   * guardian). The caller must route this row's edit affordance to the guardian
   * contact editor — the comparator editor would persist a family member the
   * order does not sequence.
   */
  guardianContactBacked: boolean
  /**
   * `true` when the row needs the user's attention. For tested parents this
   * flags an empty/incomplete parent record (no meaningful demographic yet),
   * so the card shows the "Incomplete" attention state instead of prematurely
   * claiming "Sent to lab". Proband-only (non-test-subject) rows are purely
   * informational and never need attention.
   */
  needsAttention: boolean
}

export type ComparatorListProjection = {
  /**
   * Parent items in display order. Empty array when no BIOLOGICAL_MOTHER /
   * BIOLOGICAL_FATHER rows are present (e.g. proband-only flow without
   * parent rows). The caller should still render any GUARDIAN_CONTACT
   * rows from `OrderInProgressFamilyDisplay` independently.
   */
  parentItems: ComparatorListParentItem[]
  /**
   * When `true`, the OrderInProgress flag-ON branch must defer to the
   * legacy JSX (`<FamilyCard>` + `<GuardianContactCard>`) — see the
   * module docstring's "Safety contract" section.
   */
  fallbackToLegacy: boolean
}

const MOTHER_RELATIONSHIP_LITERALS: ReadonlySet<string> = new Set(['MOTHER', 'bio_mother'])
const FATHER_RELATIONSHIP_LITERALS: ReadonlySet<string> = new Set(['FATHER', 'bio_father'])

function relationshipMatchesParent(
  value: FamilyMemberRelationship | string | null | undefined,
  set: ReadonlySet<string>
): boolean {
  if (value == null) return false
  return set.has(String(value).trim())
}

function findFirstByRelationship(
  comparators: ReadonlyArray<Comparator>,
  set: ReadonlySet<string>
): Comparator | undefined {
  for (const c of comparators) {
    if (relationshipMatchesParent(c.relationship, set)) {
      return c
    }
  }
  return undefined
}

function hasAnyMeaningfulDemographic(c: Comparator): boolean {
  const d = c.demographic ?? null
  if (!d) return false
  return Boolean(
    d.firstName?.trim() ||
    d.lastName?.trim() ||
    d.dateOfBirth?.trim() ||
    d.mrn?.trim() ||
    d.email?.trim() ||
    d.phone?.trim()
  )
}

function statusFor(
  row: OrderInProgressFamilyRow,
  comparator: Comparator,
  successStatusText: string
): { statusMessage?: string; statusVariant?: 'success' | 'info'; needsAttention?: boolean } {
  if (row.kind === 'GUARDIAN_CONTACT') {
    return {}
  }
  if (row.rowNonTestSubject) {
    if (row.treatedAsTestedComparator && hasAnyMeaningfulDemographic(comparator)) {
      return { statusMessage: successStatusText, statusVariant: 'success' }
    }
    return { statusMessage: 'Not included', statusVariant: 'info' }
  }
  // Tested-parent path. A tested parent must only show its success pill
  // once its record has real content — an empty/synthesized parent slot
  // (e.g. a trio slot the user has not filled yet) is still incomplete
  // and must say so rather than defaulting to the success pill that
  // `ComparatorCard` derives from `isTestingComparator`.
  if (!hasAnyMeaningfulDemographic(comparator)) {
    return { statusMessage: 'Incomplete', statusVariant: 'info', needsAttention: true }
  }
  // Content present: emit the caller-provided success label explicitly so
  // the draft flow ("Complete") and post-submit flow ("Sent to
  // lab") each show the right copy instead of ComparatorCard's hardcoded
  // "Sent to lab" default.
  return { statusMessage: successStatusText, statusVariant: 'success' }
}

/**
 * F.1c (GEN-2999) Slice B1: minimal placeholder comparator for a parent
 * row that has no matching `Comparator` (e.g. a trio with a parent slot the
 * user has not filled yet). Carries only the relationship so
 * `<ComparatorCard>` renders its empty state; demographic stays absent.
 */
function emptyComparatorForParentRow(kind: 'BIOLOGICAL_MOTHER' | 'BIOLOGICAL_FATHER'): Comparator {
  return { relationship: kind === 'BIOLOGICAL_MOTHER' ? 'MOTHER' : 'FATHER' }
}

/**
 * Parent side a guardian contact identifies itself as, or `null` when the
 * guardian is not a biological parent (legal guardian, grandparent, …).
 *
 * Mirrors the guardian-path test `deriveOrderInProgressFamilyRows` uses to emit
 * the proband-only parent rows in the first place: the SPA's
 * `guardianContact.relationshipToPatient` vocabulary (UPPER_SNAKE) or the IMDB
 * `family_member.relationship` lineage value echoed on
 * `primaryConsent.familyMember`.
 */
function guardianParentSide(
  co: CanonicalOrderPayload | null | undefined
): 'BIOLOGICAL_MOTHER' | 'BIOLOGICAL_FATHER' | null {
  if (!co) return null
  const gcRelRaw = (co.guardianContact?.relationshipToPatient ?? '').trim()
  const gcRel = gcRelRaw.toUpperCase()
  const pcmRel = (co.primaryConsent?.familyMember?.relationship ?? '').trim()
  if (gcRel === 'MOTHER') return 'BIOLOGICAL_MOTHER'
  if (gcRel === 'FATHER') return 'BIOLOGICAL_FATHER'
  // Only consult the primary-consent lineage echo when guardian relationship
  // is absent. An explicit non-parent guardian (e.g. GRANDMOTHER) must not be
  // coerced into a biological parent side by stale lineage data.
  if (gcRelRaw === '') {
    if (pcmRel === 'bio_mother') return 'BIOLOGICAL_MOTHER'
    if (pcmRel === 'bio_father') return 'BIOLOGICAL_FATHER'
  }
  return null
}

function demographicFromGuardianContact(gc: CanonicalGuardianContact): ComparatorDemographic | null {
  const stated: ComparatorDemographic = {}
  const fields: ReadonlyArray<keyof ComparatorDemographic & keyof CanonicalGuardianContact> = [
    'firstName',
    'lastName',
    'dateOfBirth',
    'email',
    'phone',
  ]
  for (const field of fields) {
    const value = gc[field]
    if (typeof value === 'string' && value.trim() !== '') {
      stated[field] = value
    }
  }
  return Object.keys(stated).length > 0 ? stated : null
}

/**
 * Comparator for a proband-only parent row whose person lives on
 * `guardianContact`, not in `comparators[]`.
 *
 * A proband-only order sequences nobody but the patient, so a mother/father who
 * is only the consenting guardian is never persisted as a family member — the
 * server-side read projection drops `MOTHER` / `FATHER` guardians from
 * `comparators[]` and the guardian's details survive only on
 * `guardianContact`. Without this the row paired with the empty placeholder
 * from {@link emptyComparatorForParentRow} and rendered the guardian's own card
 * as "None entered" while their details sat one key away on the payload.
 *
 * Returns `null` unless the guardian identifies as this row's parent side and
 * actually names someone, so a non-parent guardian or a
 * contact-marked-unavailable row keeps the honest empty state.
 */
function guardianBackedComparatorForParentRow(
  kind: 'BIOLOGICAL_MOTHER' | 'BIOLOGICAL_FATHER',
  co: CanonicalOrderPayload | null | undefined
): Comparator | null {
  if (!co?.guardianContact) return null
  if (guardianParentSide(co) !== kind) return null
  const demographic = demographicFromGuardianContact(co.guardianContact)
  if (!demographic) return null
  return { ...emptyComparatorForParentRow(kind), demographic }
}

function buildParentItem(
  row: OrderInProgressFamilyRow,
  comparator: Comparator,
  successStatusText: string,
  guardianContactBacked: boolean
): ComparatorListParentItem | null {
  if (row.kind === 'GUARDIAN_CONTACT') return null
  const rowKind = row.kind
  const editTarget: 'mother' | 'father' = rowKind === 'BIOLOGICAL_MOTHER' ? 'mother' : 'father'
  const { statusMessage, statusVariant, needsAttention } = statusFor(row, comparator, successStatusText)
  const effectiveComparator: Comparator = {
    ...comparator,
    isPrimaryGuardian: row.showPrimaryGuardianBadge,
    isTestingComparator:
      typeof comparator.isTestingComparator === 'boolean' ? comparator.isTestingComparator : !row.rowNonTestSubject,
  }
  return {
    rowKind,
    editTarget,
    comparator: effectiveComparator,
    isPrimaryGuardian: row.showPrimaryGuardianBadge,
    guardianContactBacked,
    statusMessage,
    statusVariant,
    needsAttention: needsAttention === true,
  }
}

export interface ProjectComparatorsToRowsOptions {
  /**
   * F.1c (GEN-2999) Slice B1: when `true`, an unmatched BIOLOGICAL_MOTHER /
   * BIOLOGICAL_FATHER row is emitted with a synthesized empty `Comparator`
   * (see {@link emptyComparatorForParentRow}) instead of signalling
   * `fallbackToLegacy`. OrderInProgress sets this so `<ComparatorList>` is
   * the sole render path — there is no legacy `<FamilyCard>` JSX left to
   * fall back to. Default (`false`) preserves the F.1b safety contract used
   * by OrderStart, which still keeps its legacy branch.
   */
  synthesizeEmptyForUnmatchedParents?: boolean
  /**
   * Success pill label for a tested parent whose record has content.
   * Defaults to `"Sent to lab"` (post-submit copy). Draft pages
   * (`OrderStart`) pass `"Complete"` so a filled-but-not-yet-sent
   * parent doesn't prematurely claim it was sent to the lab.
   */
  successStatusText?: string
}

/**
 * Pure helper. Given the existing family-display rows (the source of
 * truth) and the canonical `comparators[]` array, produce the parent
 * `ComparatorCard` items in display order.
 *
 * `co` supplies context that lives outside `comparators[]` — today the
 * `guardianContact` block that owns the person behind a proband-only
 * mother/father row (see {@link guardianBackedComparatorForParentRow}).
 */
export function projectComparatorsToOrderInProgressRows(
  display: OrderInProgressFamilyDisplay,
  comparators: ReadonlyArray<Comparator> | null | undefined,
  co?: CanonicalOrderPayload | null | undefined,
  options?: ProjectComparatorsToRowsOptions
): ComparatorListProjection {
  const emitEmpty = options?.synthesizeEmptyForUnmatchedParents === true
  const successStatusText = options?.successStatusText ?? 'Sent to lab'
  const hasComparators = Array.isArray(comparators) && comparators.length > 0
  const parentRowKinds = display.rows
    .map((r) => r.kind)
    .filter((k): k is 'BIOLOGICAL_MOTHER' | 'BIOLOGICAL_FATHER' => k !== 'GUARDIAN_CONTACT')

  if (parentRowKinds.length === 0) {
    // No parent rows to project (e.g. proband-only with guardian only):
    // the caller renders the guardian card and skips the ComparatorList
    // parent items entirely. No fallback needed.
    return { parentItems: [], fallbackToLegacy: false }
  }

  if (!hasComparators && !emitEmpty) {
    return { parentItems: [], fallbackToLegacy: true }
  }

  const items: ComparatorListParentItem[] = []
  for (const row of display.rows) {
    if (row.kind === 'GUARDIAN_CONTACT') continue
    const set = row.kind === 'BIOLOGICAL_MOTHER' ? MOTHER_RELATIONSHIP_LITERALS : FATHER_RELATIONSHIP_LITERALS
    const match = hasComparators ? findFirstByRelationship(comparators as ReadonlyArray<Comparator>, set) : undefined
    // A non-test-subject parent row may be backed by `guardianContact` rather
    // than by a comparator, in which case it is renderable even with no match.
    const guardianBacked = row.rowNonTestSubject ? guardianBackedComparatorForParentRow(row.kind, co) : null
    const base = match ?? (emitEmpty || guardianBacked ? emptyComparatorForParentRow(row.kind) : undefined)
    if (!base) {
      return { parentItems: [], fallbackToLegacy: true }
    }
    // `guardianContact` wins field-by-field over any matching comparator: it is
    // the only guardian surface the provider edits, so a stale parent echo must
    // not outrank what they just typed. Fields it does not state (MRN, specimen,
    // ids) stay on the comparator.
    const resolved: Comparator = guardianBacked
      ? { ...base, demographic: { ...(base.demographic ?? {}), ...guardianBacked.demographic } }
      : base
    const built = buildParentItem(row, resolved, successStatusText, guardianBacked != null)
    if (built) items.push(built)
  }
  return { parentItems: items, fallbackToLegacy: false }
}
